<template>
  <div class="commandMenu">
    <div v-FocusTrap :class="{ 'p-3': !inline }">
      <InputGroup class="w-100">
        <InputGroupAddon class="p-0">
          <i class="pi pi-search" />
        </InputGroupAddon>
        <InputText
          v-model="searchText"
          :size="!inline ? 'large' : undefined"
          :autofocus="true"
          :class="{ 'p-valid': state === true, 'p-invalid': state === false }"
          :placeholder="t('modules.commandMenu.views.CommandMenu.search')"
          class="w-100"
        />
        <InputGroupAddon v-if="useSearchOption != null">
          <LISAButton
            :label="t('components.SearchList.buttonOptionsText')"
            class="p-button-outlined p-button-secondary"
            :text="true"
            @click="overlayRef?.show"
          />
          <Popover ref="overlayRef" class="SearchListOverlayPanelScope">
            <div>
              {{ t("components.SearchList.DDHeadline") }}
            </div>
            <div>
              <div v-for="(option, index) in searchOptions" :key="`Check${index}`" class="pb-1">
                <Checkbox
                  :model-value="showSearchOption(option.type)"
                  binary
                  @update:model-value="setSearchOption(option.type, $event)"
                />
                {{ option.name }}
              </div>
            </div>
          </Popover>
        </InputGroupAddon>
      </InputGroup>
    </div>
    <Listbox
      ref="listboxRef"
      v-model="selectedResult"
      option-label="name"
      :multiple="multiple"
      :options="results"
      :scroll-height="scrollHeight"
      :pt="
        hideBorder
          ? {
              root: {
                style: {
                  border: '0',
                },
              },
            }
          : undefined
      "
      @click="$emit('selected')"
    >
      <template #option="slotProps">
        <InfoTooltip
          v-if="slotProps.option.searchResultType !== GSSearchResultType.LOADING"
          :text="getOverlayText(slotProps.option)"
          :disabled="getOverlayText(slotProps.option) == null"
          :title="t('modules.commandMenu.views.CommandMenu.description')"
          :visible-time-in-ms="100"
        >
          <template #activator>
            <div class="flex justify-content-between">
              <i
                class="mr-2"
                :class="getIcon(slotProps.option.searchResultType, slotProps.option.meta)"
              />
              <div>
                <span class="bold">
                  {{ slotProps.option.name }}
                </span>

                <div class="text-truncate w-100">
                  {{ getOverlayText(slotProps.option) }}
                </div>
              </div>
            </div>
          </template>
        </InfoTooltip>
      </template>
    </Listbox>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, watch, ref, type PropType } from "vue";
import Listbox from "primevue/listbox";
import vFocusTrap from "primevue/focustrap";
import InputText from "primevue/inputtext";
import { GSSearchResultType, GSIncludeType } from "@/graphql";
import { tileConfigs } from "@/modules/tiles/tileConfigs";
import InputGroup from "primevue/inputgroup";
import InputGroupAddon from "primevue/inputgroupaddon";
import LISAButton from "@LISAPrime/LISAButton.vue";
import InfoTooltip from "@/components/shared/InfoTooltip.vue";
import Popover from "primevue/popover";
import Checkbox from "primevue/checkbox";
import { useAppTranslate } from "@/language/staticTranslations";
import type { SearchOptionMetaType, SearchOptionType } from "../helper/CommandMenuType";

const emit = defineEmits<{
  selected: [];
}>();

const props = withDefaults(
  defineProps<{
    results: SearchOptionType[];
    inline?: boolean;
    state?: boolean;
    scrollHeight?: string;
    hideBorder?: boolean;
    multiple?: boolean;
  }>(),
  {
    inline: false,
    state: undefined,
    scrollHeight: "60vh",
    hideBorder: false,
    multiple: false,
  },
);

const { t } = useAppTranslate();

const searchOptions = computed(() =>
  Object.keys(GSIncludeType).map((type) => ({
    type: type as GSIncludeType,
    name: t(`modules.commandMenu.views.CommandMenu.type.${type}`),
  })),
);

const useSearchOption = defineModel("useSearchOption", {
  type: Array as () => GSIncludeType[],
  required: false,
  default: undefined,
});

function showSearchOption(option: GSIncludeType) {
  return useSearchOption.value.includes(option);
}

function setSearchOption(option: GSIncludeType, value: boolean) {
  if (value) {
    useSearchOption.value.push(option);
  } else {
    useSearchOption.value = useSearchOption.value.filter((o) => o !== option);
  }
}

const selectedResult = defineModel("selectedResult", {
  type: [Object, Array] as PropType<SearchOptionType[] | SearchOptionType | undefined>,
  required: false,
  default: undefined,
});

const searchText = defineModel("searchText", {
  type: String,
  required: true,
});

const icons: Record<GSSearchResultType, string | ((searchResult: SearchOptionMetaType) => string)> =
  {
    [GSSearchResultType.MENU]: "fas fa-play",
    [GSSearchResultType.EVENT]: "fas fa-tag",
    [GSSearchResultType.ACTION]: "far fa-comment",
    [GSSearchResultType.LOADING]: "fas fa-spinner fa-spin",
    [GSSearchResultType.BOARD]: "fas fa-th",
    [GSSearchResultType.EASTER_EGG]: "fas fa-egg",
    [GSSearchResultType.TILE]: (searchResult: SearchOptionMetaType) => {
      const meta = searchResult;

      if (meta?.__typename !== "GSSearchResultTileMeta") {
        return "";
      }

      return tileConfigs[meta.tileType]?.icon ?? "";
    },
  };

function getIcon(resultType: GSSearchResultType, meta: SearchOptionMetaType) {
  const icon = icons[resultType];
  if (typeof icon === "string") {
    return icons[resultType];
  }
  return icon(meta);
}
const overlayRef = ref<InstanceType<typeof Popover>>();

function getOverlayText(option: SearchOptionType | undefined) {
  const meta = option?.meta;
  switch (meta?.__typename) {
    case "GSSearchResultEventMeta":
      return meta.description;
    case "GSSearchResultActionMeta":
      return meta.description;
    default:
      return undefined;
  }
}

watch(
  computed(() => props.results),
  () => {
    if (props.results.length === 0) {
      selectedResult.value = undefined;
    } else if (
      props.multiple !== true &&
      Array.isArray(selectedResult.value) === false &&
      (selectedResult.value == null || !props.results.includes(selectedResult.value))
    ) {
      selectedResult.value = props.results[0];
    }
  },
);

function keyPressed(e: KeyboardEvent) {
  if (props.multiple === true) {
    return;
  }
  if (e.key === "Enter") {
    emit("selected");
    return;
  }
  let index = Array.isArray(selectedResult.value) ? 1 : (selectedResult.value?.index ?? 1);
  if (e.key === "ArrowUp") {
    if (index <= 0) {
      index = props.results.length - 1;
    } else {
      index -= 1;
    }
    selectedResult.value = props.results[index];
  }
  if (e.key === "ArrowDown") {
    if (index >= props.results.length - 1) {
      index = 0;
    } else {
      index += 1;
    }
    selectedResult.value = props.results[index];
  }
}

function globalKeyHandler(e: KeyboardEvent) {
  keyPressed(e);
}

onMounted(() => {
  window.addEventListener("keydown", globalKeyHandler, true);
});

onBeforeUnmount(() => {
  window.removeEventListener("keydown", globalKeyHandler, true);
});
</script>

<style lang="scss" scoped>
.commandMenu {
  background-color: white;
  border-radius: 10px;

  .results {
    border-top: 1px solid #ccc;
    padding: 1em;
  }
}
</style>
